import type { Components, Theme } from '@mui/material/styles';

import { colors, inputHeight, radius, semantic } from './tokens';

/**
 * Component overrides, ported from Web/src/assets/themes/components/Mui*.ts.
 *
 * Web has 43 overrides; this app needs the subset it actually renders. The point
 * of porting rather than inventing is that before this file existed, dialogs,
 * inputs, tables and menus rendered as *stock MUI* — default 4px radii,
 * elevation shadows, MUI's own type metrics — while every hand-styled component
 * rendered the TimeLog spec. Two visual languages in one app.
 *
 * Where Web has no override to copy (MuiTable — Web uses DataGrid instead), the
 * values are derived from its MuiDataGrid spec and the derivation is noted.
 */

const iconOpticalSize = (size: number): Record<string, string> => ({
  fontSize: `${size}px`,
  fontVariationSettings: `'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' ${size}`,
});

export const components: Components<Theme> = {
  /**
   * Ripples are off across the whole design system, and the focus outline is
   * defined here so every button, menu item, tab and icon button inherits it.
   * Before this, `--focus-ring` was declared in tokens.css and referenced
   * nowhere — the app had no visible focus indicator at all.
   */
  MuiButtonBase: {
    defaultProps: { disableRipple: true },
    styleOverrides: {
      root: {
        '&.Mui-focusVisible': {
          outline: `2px solid ${semantic.fg1}`,
          outlineOffset: '1px',
        },
        '&.MuiButtonBase-root': {
          textTransform: 'none',
          lineHeight: 'normal',
        },
      },
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true, size: 'small' },
    styleOverrides: {
      root: {
        borderRadius: radius.pill,
        paddingLeft: 'var(--spacing-unit-2)',
        paddingRight: 'var(--spacing-unit-2)',
        variants: [
          {
            props: { size: 'tiny' },
            style: {
              height: 'var(--input-height-tiny)',
              fontSize: 'var(--font-size-label)',
              paddingLeft: 'var(--spacing-unit-1)',
              paddingRight: 'var(--spacing-unit-1)',
              '& .material-symbols-rounded': iconOpticalSize(16),
            },
          },
          {
            props: { size: 'small' },
            style: {
              height: 'var(--input-height-small)',
              '& .material-symbols-rounded': iconOpticalSize(20),
            },
          },
          {
            props: { size: 'medium' },
            style: {
              height: 'var(--input-height-medium)',
              '& .material-symbols-rounded': iconOpticalSize(24),
            },
          },
          {
            props: { size: 'large' },
            style: {
              height: 'var(--input-height-large)',
              '& .material-symbols-rounded': iconOpticalSize(32),
            },
          },
          { props: { variant: 'text', color: 'secondary' }, style: { color: colors.fog[100] } },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              backgroundColor: colors.fog[100],
              '&:hover': { backgroundColor: colors.fog[80] },
              '&[aria-pressed="true"], &[aria-expanded="true"]': {
                backgroundColor: colors.blue[10],
                color: colors.blue[80],
              },
            },
          },
          {
            props: { variant: 'outlined', color: 'secondary' },
            style: {
              borderColor: colors.fog[100],
              color: colors.fog[100],
              '&:hover': { backgroundColor: colors.fog[10] },
              '&[aria-pressed="true"], &[aria-expanded="true"]': {
                backgroundColor: colors.blue[10],
                color: colors.blue[80],
              },
            },
          },
          {
            props: { rounded: true },
            style: {
              borderRadius: radius.large,
              paddingLeft: 'var(--spacing-unit-2)',
              paddingRight: 'var(--spacing-unit-2)',
            },
          },
        ],
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        height: inputHeight.medium,
        width: inputHeight.medium,
        variants: [
          {
            props: { size: 'tiny' },
            style: { height: inputHeight.tiny, width: inputHeight.tiny },
          },
          {
            props: { size: 'small' },
            style: { height: inputHeight.small, width: inputHeight.small },
          },
          {
            props: { size: 'large' },
            style: { height: inputHeight.large, width: inputHeight.large },
          },
        ],
      },
    },
  },

  /**
   * `fog-20`, not `black-40`. The darker border was the previous value here and
   * made every card read heavier than the same card in Web.
   */
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: semantic.bgPanel,
        border: `var(--border-width-hairline) solid ${colors.fog[20]}`,
        variants: [{ props: { padded: true }, style: { padding: 'var(--spacing-unit-2)' } }],
      },
    },
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        borderRadius: radius.xLarge,
        border: `var(--border-width-thin) solid ${colors.fog[20]}`,
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 'var(--spacing-unit-3)',
        '&:last-child': { paddingBottom: 'var(--spacing-unit-3)' },
      },
    },
  },

  /**
   * Top-aligned rather than centred, with the two height media queries from the
   * design system so a tall dialog on a short screen still fits.
   */
  MuiDialog: {
    defaultProps: { maxWidth: 'xs' },
    styleOverrides: {
      paper: {
        borderRadius: radius.medium,
        backgroundColor: semantic.bgPanel,
        border: `var(--border-width-thin) solid ${semantic.borderSubtle}`,
      },
      container: {
        alignItems: 'flex-start',
        marginTop: '5vh',
        maxHeight: '85vh',
        '@media (max-height: 800px)': { maxHeight: '95vh', marginTop: '5vh' },
        '@media (max-height: 600px)': {
          maxHeight: '100vh',
          marginTop: 0,
          alignItems: 'center',
        },
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: { padding: 'var(--spacing-unit-3)', paddingBottom: 'var(--spacing-unit-2)' },
    },
  },

  MuiDialogContent: {
    styleOverrides: { root: { padding: 'var(--spacing-unit-3)', paddingTop: 0 } },
  },

  MuiDialogActions: {
    styleOverrides: { root: { padding: 'var(--spacing-unit-3)', paddingTop: 0, gap: 8 } },
  },

  MuiOutlinedInput: {
    defaultProps: { notched: false },
    styleOverrides: {
      root: {
        borderRadius: radius.medium,
        variants: [{ props: { pilled: true }, style: { borderRadius: radius.pill } }],
      },
    },
  },

  MuiInputBase: {
    defaultProps: { size: 'medium' },
    styleOverrides: {
      root: { font: 'inherit' },
      input: { '&::placeholder': { color: semantic.fg3, opacity: 1 } },
    },
  },

  MuiInputLabel: { defaultProps: { shrink: false } },

  MuiMenu: {
    styleOverrides: { list: { padding: 'var(--spacing-unit-half)' } },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.medium,
        minHeight: inputHeight.small,
        padding: theme.spacing(1),
        gap: theme.spacing(1),
        '&:not(:first-of-type)': { marginTop: theme.spacing(0.5) },
        '&.Mui-selected': {
          backgroundColor: colors.fog[20],
          '&:hover': { backgroundColor: colors.fog[40] },
          '&:focus:not(:hover)': { backgroundColor: colors.fog[40] },
        },
        '&:hover:not(.Mui-selected), &:focus:not(.Mui-selected)': {
          backgroundColor: colors.fog[20],
        },
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      label: { font: 'inherit', display: 'inline-flex' },
      root: {
        borderRadius: radius.pill,
        minWidth: 32, // forces short labels to read as round chips
        variants: [
          {
            props: { size: 'small' },
            style: { '& .MuiChip-label': { fontSize: 'var(--font-size-label)' } },
          },
          {
            props: { color: 'purple' },
            style: { backgroundColor: colors.purple[20], color: colors.purple[80] },
          },
          {
            props: { color: 'fog' },
            style: { backgroundColor: colors.fog[20], color: colors.fog[100] },
          },
          {
            props: { color: 'coral' },
            style: { backgroundColor: colors.coral[20], color: colors.coral[80] },
          },
          {
            props: { color: 'maroon' },
            style: { backgroundColor: colors.maroon[20], color: colors.maroon[80] },
          },
          {
            props: { color: 'blue' },
            style: {
              '&.MuiChip-filled': { backgroundColor: colors.blue[10], color: colors.blue[100] },
              '&.MuiChip-outlined': {
                backgroundColor: colors.blue[10],
                borderColor: colors.blue[80],
                color: colors.blue[80],
              },
            },
          },
          {
            props: { color: 'navy' },
            style: {
              '&.MuiChip-filled': { backgroundColor: colors.navy[10], color: colors.navy[100] },
              '&.MuiChip-outlined': {
                backgroundColor: colors.navy[10],
                borderColor: colors.navy[100],
                color: colors.navy[100],
              },
            },
          },
          {
            props: { monoText: true },
            style: { '& .MuiChip-label': { fontFamily: 'var(--font-family-mono)' } },
          },
        ],
      },
      colorDefault: {
        backgroundColor: colors.black[80],
        color: colors.black[10],
        '&.MuiChip-outlined': {
          backgroundColor: colors.black[10],
          color: colors.black[60],
          borderColor: colors.black[60],
          '&.MuiChip-clickable:hover': { backgroundColor: colors.black[20] },
        },
      },
      colorError: { backgroundColor: colors.error[10], color: colors.error[100] },
      colorWarning: { backgroundColor: colors.warning[10], color: colors.warning[100] },
      colorInfo: { backgroundColor: colors.info[10], color: colors.info[100] },
      colorSuccess: { backgroundColor: colors.success[10], color: colors.success[100] },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: radius.medium,
        // Paper would otherwise lend it a border.
        '&:not(.MuiAlert-outlined)': { border: 'none' },
      },
      standardError: {
        backgroundColor: colors.error[10],
        color: colors.error[100],
        '& .MuiAlert-icon': { color: colors.error[100] },
      },
      standardWarning: {
        backgroundColor: colors.warning[10],
        color: colors.warning[100],
        '& .MuiAlert-icon': { color: colors.warning[100] },
      },
      standardInfo: {
        backgroundColor: colors.info[10],
        color: colors.info[100],
        '& .MuiAlert-icon': { color: colors.info[100] },
      },
      standardSuccess: {
        backgroundColor: colors.success[10],
        color: colors.success[100],
        '& .MuiAlert-icon': { color: colors.success[100] },
      },
    },
  },

  MuiSkeleton: {
    defaultProps: { animation: 'wave', variant: 'rounded' },
    styleOverrides: {
      root: {
        variants: [{ props: { variant: 'pilled' }, style: { borderRadius: radius.pill } }],
      },
    },
  },

  MuiStack: { defaultProps: { useFlexGap: true, gap: 1 } },

  MuiDivider: { styleOverrides: { root: { borderColor: semantic.borderDivider } } },

  /**
   * Derived, not ported: Web renders tables through MuiDataGrid, so these values
   * come from its DataGrid spec — 44px header, 56px row, header label in the
   * `label` variant at black-60, fog-10 row hover.
   */
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottomColor: semantic.borderDivider,
        padding: 'var(--spacing-unit-1) var(--spacing-unit-2)',
      },
      head: {
        height: 44,
        fontSize: 'var(--font-size-label)',
        fontWeight: 400,
        color: semantic.fg3,
        backgroundColor: semantic.bgSunken,
      },
      body: { height: 56 },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: { '&:hover': { backgroundColor: colors.fog[10] } },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: radius.pill, backgroundColor: semantic.bgSunken },
      bar: { borderRadius: radius.pill },
    },
  },

  // First used by the timeline zoom control. MUI's default palette never
  // renders: track/thumb take the brand pink, the rail the sunken grey, and
  // focus matches the MuiButtonBase outline (Slider is not a ButtonBase, so it
  // does not inherit that one).
  MuiSlider: {
    styleOverrides: {
      root: { color: semantic.fgBrand },
      rail: { color: semantic.bgSunken, opacity: 1 },
      thumb: {
        boxShadow: 'none',
        '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: 'none' },
        '&.Mui-focusVisible': {
          outline: `2px solid ${semantic.fg1}`,
          outlineOffset: '1px',
        },
        // MUI's invisible 42×42 touch target overhangs the rail by 15px each
        // side and swallows clicks on anything sitting next to the slider —
        // the timeline zoom's ± buttons, concretely. This is a desktop app;
        // a 20px halo is plenty.
        '&::after': { width: 20, height: 20 },
      },
    },
  },

  /*
    The design system's light tooltip, as TimeLog Web styles it
    (Web/src/assets/themes/components/MuiTooltip.ts, 2026-10-12, the pilot's
    ask): off-white paper, dark text, a soft shadow, small rounded corners, an
    arrow. It was a dark chip here.
  */
  MuiTooltip: {
    defaultProps: { placement: 'top', enterDelay: 300, arrow: true },
    styleOverrides: {
      tooltip: {
        backgroundColor: colors.black[10],
        color: 'var(--fg-1)',
        fontSize: 'var(--font-size-label)',
        borderRadius: radius.small,
        boxShadow: 'var(--shadow-tooltip)',
        padding: '8px 12px',
      },
      arrow: {
        color: colors.black[10],
      },
    },
  },

  MuiTypography: {
    defaultProps: { variant: 'bodyRegular' },
    styleOverrides: {
      root: {
        variants: [
          { props: { uppercase: true }, style: { textTransform: 'uppercase' } },
          // The design system's headings are navy, not black — this is the token
          // that `#1A1A70` was hardcoded in place of across three files.
          { props: { color: 'primary' }, style: { color: semantic.fgHeading } },
        ],
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      colorPrimary: {
        backgroundColor: semantic.bgPanel,
        color: semantic.fg1,
        borderBottom: `var(--border-width-thin) solid ${semantic.borderDivider}`,
      },
    },
  },
};
